import { OrganizationsService, useApiEffect } from '@localstack/services';
import { useEffect, useRef, useState } from 'react';

import { CombinedUserInfo, SSOError, SSOParams, isSSOPostMessagePayload } from '@localstack/types';

import { AppRoute, BASE_URL, COGNITO_BASE_URL, COGNITO_CUSTOM_BASE_URL, COGNITO_CLIENT_ID } from '~/config';
import { formatSSOError } from '~/util/ssoErrors';
import { createNewPopup } from '~/util/window';

import { useAuthProvider } from './useAuthProvider';

type SSOStartReturnType = {
  isIdpLoading: boolean;
  initSSOFlow: (ssoParams: Optional<SSOParams>) => Promise<void>;
  selectedIdpName: Optional<string>;
};

type UseSSOStartParams = {
  onSuccess?: (userInfo: CombinedUserInfo) => void;
  onError?: (error: SSOError) => void;
};

const getCognitoBaseUrl = (orgId: string) => (orgId === 'public' ? COGNITO_CUSTOM_BASE_URL : COGNITO_BASE_URL);

const buildSSOLink = ({ orgId, idpName }: SSOParams) => {
  // Cognito IDPs are named `{orgId}-{idpName}` and looked up by name via `identity_provider`.
  // Google is the exception: Cognito reserves that provider name, so `public-google` lives in its
  // `idp_identifier` (tag) instead and has to be looked up that way.
  const isPublicGoogleIdp = orgId === 'public' && idpName === 'google';
  const idpNameOrIdentifier = isPublicGoogleIdp ? 'idp_identifier' : 'identity_provider';

  return (
    `${getCognitoBaseUrl(orgId)}/oauth2/authorize?response_type=code` +
    `&client_id=${COGNITO_CLIENT_ID}&redirect_uri=${BASE_URL}${AppRoute.SSO_CALLBACK}` +
    `&${idpNameOrIdentifier}=${orgId}-${idpName}`
  );
};

export const useSSOStart = (params: UseSSOStartParams): SSOStartReturnType => {
  const { onSuccess, onError } = params;
  const [windowObj, setWindowObj] = useState<Window | null>(null);
  const { userInfo, reloadUserInfo } = useAuthProvider();
  const [selectedIdpName, setSelectedIdpName] = useState<Optional<string>>(undefined);

  const selectedSSOParams = useRef<Optional<SSOParams>>(undefined);

  const {
    getIdentityProvider,
    isLoading: isIdpLoading,
    error: apiError,
  } = useApiEffect(OrganizationsService, ['getIdentityProvider'], { suppressErrors: true });

  const initSSOFlow = async ({ idpName, orgId }: SSOParams) => {
    if (!idpName || !orgId) {
      onError?.({
        type: 'idp-error',
        message: 'Something went wrong.',
      });
      return;
    }
    setSelectedIdpName(idpName);
    selectedSSOParams.current = { idpName, orgId };

    const popup = createNewPopup(window, AppRoute.SSO_LOADING);

    if (!popup) {
      onError?.({
        type: 'popup-error',
        message: 'Pop-up is blocked. Please enable pop-ups and try again',
      });
      return;
    }

    const idp = await getIdentityProvider(orgId, idpName);

    if (!idp) {
      popup.close();
      onError?.({
        type: 'idp-error',
        message: 'Something went wrong. Please contact your administrator to verify your IDP settings.',
      });
      return;
    }

    if (apiError) {
      popup.close();
      onError?.({
        type: 'idp-error',
        message: apiError.message,
      });
      return;
    }

    const ssoLink = buildSSOLink({ idpName, orgId });

    popup.location.href = ssoLink;
    popup.focus();
    setWindowObj(popup);
  };

  const closeWindow = () => windowObj?.close();

  useEffect(() => {
    if (!userInfo || !windowObj) {
      return;
    }
    // only redirect if sso window is open and userInfo is available
    onSuccess?.(userInfo);
    closeWindow();
  }, [userInfo]);

  useEffect(() => {
    if (!windowObj) {
      return;
    }

    const messageEventHandler = async (event: MessageEvent) => {
      if (event.origin !== BASE_URL) {
        return;
      }

      if (!isSSOPostMessagePayload(event.data)) {
        return;
      }

      if (event.data.messageType === 'sso-error') {
        onError?.(formatSSOError(event.data.error, selectedSSOParams.current));
        closeWindow();
      }
      if (event.data.messageType === 'sso-success') {
        if (userInfo) {
          // if userInfo is available, call the success callback here
          // or it'll be handled in the useEffect
          onSuccess?.(userInfo);
          closeWindow();
        } else {
          await reloadUserInfo();
        }
      }
    };

    window.addEventListener('message', messageEventHandler);
    return () => window.removeEventListener('message', messageEventHandler);
  }, [windowObj]);

  return { isIdpLoading, initSSOFlow, selectedIdpName };
};
